import { useState, useEffect } from 'react';
import kit from './kit.module.css';
import styles from './HumanInTheLoopDemoAgentic.module.css';

type WorkflowState = 'reviewing' | 'approved' | 'complete' | 'cancelled';
type StepStatus = 'complete' | 'paused' | 'waiting' | 'cancelled' | 'active';

interface Step {
  time: string;
  label: string;
  status: StepStatus;
}

const DOT: Record<StepStatus, string> = {
  complete: kit.logDotDone,
  paused: kit.logDotActive,
  active: kit.logDotActive,
  waiting: '',
  cancelled: '',
};

function StatusChip({ status }: { status: StepStatus }) {
  switch (status) {
    case 'complete':
      return <span className={`${kit.chip} ${kit.chipGood}`}>Done</span>;
    case 'paused':
      return <span className={`${kit.chip} ${kit.chipWarn}`}>Needs you</span>;
    case 'active':
      return <span className={kit.chip}>Running</span>;
    case 'cancelled':
      return <span className={kit.chip}>Cancelled</span>;
    default:
      return <span className={`${kit.chip} ${styles.chipMuted}`}>Queued</span>;
  }
}

export function HumanInTheLoopDemoAgentic() {
  const [workflowState, setWorkflowState] = useState<WorkflowState>('reviewing');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (workflowState !== 'approved') return;
    const t = setTimeout(() => setWorkflowState('complete'), 1200);
    return () => clearTimeout(t);
  }, [workflowState]);

  const steps: Step[] = [
    { time: '09:40', label: 'Collected jobseeker information', status: 'complete' },
    { time: '09:41', label: 'Generated CV draft', status: 'complete' },
    {
      time: '09:41',
      label: 'Send to employer',
      status:
        workflowState === 'cancelled' ? 'cancelled'
        : workflowState === 'reviewing' ? 'paused'
        : 'complete',
    },
    {
      time: workflowState === 'complete' ? '09:42' : '--:--',
      label: 'Archive case',
      status:
        workflowState === 'complete' ? 'complete'
        : workflowState === 'approved' ? 'active'
        : 'waiting',
    },
  ];

  return (
    <div className={kit.root}>
      <div className={kit.row}>
        <span className={kit.label}>AGENT WORKFLOW</span>
        <span className={kit.chip}>CV placement · Sarah Mitchell</span>
      </div>

      <div className={kit.log}>
        {steps.map((step, i) => (
          <div key={i} className={kit.logRow}>
            <span className={kit.logTime}>{step.time}</span>
            <span className={`${kit.logDot} ${DOT[step.status]}`} />
            <span
              className={
                step.status === 'waiting' ? styles.muted
                : step.status === 'cancelled' ? styles.struck
                : step.status === 'paused' ? styles.strong
                : undefined
              }
            >
              {step.label}
            </span>
            <StatusChip status={step.status} />
          </div>
        ))}
      </div>

      {workflowState === 'reviewing' && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Paused for approval</span>
            <span>Step 03 / 04</span>
          </div>
          <h3 className={kit.proposalTitle}>Send Sarah Mitchell's CV to TechCorp Ltd</h3>
          <dl className={kit.fields}>
            <dt>To</dt>
            <dd>hr@techcorp.com</dd>
            <dt>Role</dt>
            <dd>Senior Care Coordinator</dd>
            <dt>Summary</dt>
            <dd>5 years experience · Manchester · Available immediately</dd>
          </dl>
          <div className={`${kit.row} ${styles.actions}`}>
            <button
              type="button"
              className={`${kit.btn} ${kit.btnApprove}`}
              onClick={() => setWorkflowState('approved')}
            >
              Approve &amp; send
            </button>
            <button
              type="button"
              className={`${kit.btn} ${kit.btnEdit}`}
              onClick={() => setEditing(true)}
            >
              Edit details
            </button>
            <button
              type="button"
              className={`${kit.btn} ${kit.btnGhost}`}
              onClick={() => setWorkflowState('cancelled')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <p className={kit.feedback} aria-live="polite">
        {workflowState === 'reviewing' && editing && 'Details opened for editing. The agent stays paused until you approve.'}
        {workflowState === 'reviewing' && !editing && 'The agent stopped before the irreversible step. It will not send anything without you.'}
        {workflowState === 'approved' && 'Sent after your approval. Archiving the case...'}
        {workflowState === 'complete' && 'Workflow complete. Every step after the checkpoint ran with your sign-off.'}
        {workflowState === 'cancelled' && 'Workflow paused - the CV was not sent.'}
      </p>
    </div>
  );
}
